import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import i18n, { TFunction, changeLanguage } from 'i18next';
import { FC, ReactNode, createContext, useCallback, useContext, useEffect } from 'react';
import { initReactI18next, useTranslation } from 'react-i18next';

import { AuthContext } from 'src/contexts/AuthContext';
import useLocalStorage from 'src/hooks/useLocalstorage';

import { LanguageCategories, languageCodes } from 'src/constants/localization';
import translationCA from 'src/locales/ca.json';
import translationEN from 'src/locales/en.json';
import translationES from 'src/locales/es.json';
import translationFR from 'src/locales/fr.json';
import { DropDownCategories } from 'src/types/generic';

const resources = {
  en: {
    translation: translationEN,
  },
  es: {
    translation: translationES,
  },
  fr: {
    translation: translationFR,
  },
  ca: {
    translation: translationCA,
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: 'en',
  keySeparator: false,
  interpolation: {
    escapeValue: false,
  },
});

interface LocaleContextProviderProps {
  children: ReactNode;
}

interface LocaleContextProps {
  selectedLanguage: DropDownCategories;
  handleLanguageChange: (event: DropDownListChangeEvent) => void;
  t: TFunction<'translation', undefined>;
}

export const LocaleContext = createContext<LocaleContextProps | null>(null);

const LocaleContextProvider: FC<LocaleContextProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  // Safely get user from auth context - use useContext directly to avoid throwing errors
  // AuthContext might not be available during initial render
  const authContext = useContext(AuthContext);
  const user = authContext?.user;

  const [selectedLanguage, setSelectedLanguage] = useLocalStorage(
    'selectedLanguage',
    LanguageCategories[0]
  );

  const handleLanguageChange = useCallback(
    (event: DropDownListChangeEvent) => {
      const { value } = event;
      setSelectedLanguage(value);
      changeLanguage(languageCodes[value.value.toString() as keyof typeof languageCodes]);
    },
    [setSelectedLanguage]
  );

  useEffect(() => {
    let defaultLanguage = selectedLanguage;
    if (user?.LocaleId) {
      defaultLanguage = LanguageCategories.find(
        (ln) => ln.value === Number(user?.LocaleId)
      ) as DropDownCategories;
    }
    setSelectedLanguage(defaultLanguage);
    changeLanguage(languageCodes[defaultLanguage.value.toString() as keyof typeof languageCodes]);
  }, [selectedLanguage, setSelectedLanguage, user?.LocaleId]);

  return (
    <LocaleContext.Provider value={{ selectedLanguage, handleLanguageChange, t }}>
      {children}
    </LocaleContext.Provider>
  );
};

export default LocaleContextProvider;
